// Third-party packages.
import { TreeTableSelectionKeys } from 'primereact/treetable';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import FilterOption from '../types/instance/FilterOption';
import { FilterType } from '../typescript/Enums';
import { ACTIONS, EXPIRATION_DAYS, EXPIRATION_KEY, INSTANCE_TYPE_ORACLE } from '../component/Constants';
import * as sqlFormatter from "sql-formatter";
import { INSTANCE_TYPE_TO_QUERY_LANGUAGE } from "../constants/statements";
import Helper from "./Helper";
import { InstanceOptions } from "../container/comparison/statistics-comparison/types";
import InstanceTarget from "../types/instance/InstanceTarget";
import EventType from "../types/instance/EventType";
import {
    resolveUserTimezone as _resolveUserTimezone,
    formatDateForApi as _formatDateForApi,
    dateForPickerDisplay as _dateForPickerDisplay,
    dateFromPickerSelection as _dateFromPickerSelection,
    getDefaultPeriodDates as _getDefaultPeriodDates,
    getConvertedTimeToUTC as _getConvertedTimeToUTC,
} from './timeRangeUtils';

dayjs.extend(utc.default);
dayjs.extend(timezone.default);
dayjs.extend(customParseFormat.default);

// Functions.
export function archiverUrl(version = 1): string {
    // @ts-ignore
    const url: string | undefined = process.env.REACT_APP_API.replace('v1', `v${version}`)

    if (url) {
        if (url.endsWith('/')) {
            return url.substring(0, url.length - 1);
        }

        return url;
    }

    return '';
}

export function base64Decode(b: string) {
    return b ? atob(b) : b;
}

export function base64Encode(s: string) {
    // A null string gets converted to the base-64 equivalent of "null".
    return s ? btoa(s) : s;
}

function clearDropdownStyle(id: string): void {
    const element = document.getElementById(id);

    if (element) {
        element.classList.remove('show');
        element.setAttribute('aria-expanded', 'false');
    }
}

export function closeFilterDropdown(filterId: string, dropdownId: string): void {
    clearDropdownStyle(filterId);
    clearDropdownStyle(dropdownId);
}

/** @deprecated Use resolveUserTimezone from helpers/timeRangeUtils instead. */
export const getTimeZone = _resolveUserTimezone;

/** @deprecated Use formatDateForApi from helpers/timeRangeUtils instead. */
export const formatDateForApi = _formatDateForApi;

/** @deprecated Use dateForPickerDisplay from helpers/timeRangeUtils instead. */
export const dateForPickerDisplay = _dateForPickerDisplay;

/** @deprecated Use dateFromPickerSelection from helpers/timeRangeUtils instead. */
export const dateFromPickerSelection = _dateFromPickerSelection;

/** @deprecated Use getDefaultPeriodDates from helpers/timeRangeUtils instead. */
export const getDefaultPeriodDates = _getDefaultPeriodDates;


export function getParameter(parameters: string, name: string): string | null {
    return new URLSearchParams(parameters).get(name);
}

export function getParameterValues(parameters: string, name: string): string[] {
    return new URLSearchParams(parameters).getAll(name);
}

export function getFilterOptions(parameters: string): FilterOption[] {
    let filterOptions: FilterOption[] = [];
    filterOptions.push({
        filterType: FilterType.Waits,
        filters: getParameterValues(parameters, 'waitevent')
    });
    filterOptions.push({
        filterType: FilterType.Databases,
        filters: getParameterValues(parameters, 'database')
    });
    filterOptions.push({
        filterType: FilterType.Sessions,
        filters: getParameterValues(parameters, 'session')
    });
    filterOptions.push({
        filterType: FilterType.Clients,
        filters: getParameterValues(parameters, 'client')
    });
    filterOptions.push({
        filterType: FilterType.Users,
        filters: getParameterValues(parameters, 'username')
    });
    filterOptions.push({
        filterType: FilterType.Programs,
        filters: getParameterValues(parameters, 'program')
    });
    filterOptions.push({
        filterType: FilterType.Modules,
        filters: getParameterValues(parameters, 'module')
    });
    return filterOptions;
}

export function getFilterParameters(options: FilterOption[]): string {
    let parameters: string = '';
    let name: string | undefined = undefined;

    // Build the filter parameters for future API calls.
    options.forEach(option => {
        switch (option.filterType) {
            case FilterType.Waits:
                name = 'waitevent';
                break;

            case FilterType.Databases:
                name = 'database';
                break;

            case FilterType.Sessions:
                name = 'session';
                break;

            case FilterType.Clients:
                name = 'client';
                break;

            case FilterType.Users:
                name = 'username';
                break;

            case FilterType.Programs:
                name = 'program';
                break;

            case FilterType.Modules:
                name = 'module';
                break;

            default:
                name = undefined;
                break;
        }

        if (name) {
            option.filters.forEach(value => {
                parameters += `&${name}=${encodeURIComponent(value)}`;
            });
        }
    });

    return parameters
}

export const getStatisticsParameters = (options: string[]) => {
    let parameters: string = '';
    let value: string | null = null;
    const searchParams = new URLSearchParams(window.location.search)

    options.forEach(option => {
        value = searchParams.get(option)
        if (value) {
            parameters += `&${option}=${encodeURIComponent(value)}`;
        }
    });

    return parameters
}

export async function fetchResults(fetchArray: Promise<Response>[], throwError = true): Promise<any[]> {
    let errorIndex: number = -1;
    const responses: Response[] = await Promise.all(fetchArray);
    const promises: Promise<any>[] = await responses.map((response, index) => {
        if (!response.ok) {
            // response body is a promise at the moment that will be resolved
            // by the time console.log displays it. Unfortunately cannot throw
            // an exception with a promise as an argument.
            // console.log (response.text ());
            // throw new Error (/*response.text ()*/);

            if (errorIndex === -1) {
                errorIndex = index;
            }

            return response.text();
        }

        return response.json();
    });
    // console.log ('promises', promises);
    const results: any[] = await Promise.all(promises);
    // console.log ('results', results);

    if (errorIndex > -1 && throwError) {
        throw new Error(results[errorIndex]);
    }

    return results;
}

export function fetchWithAuthorization(url: string) {
    const savedToken = window.localStorage.getItem('dbmar-token') || '';
    const headers: Record<string, string> = {};

    if (savedToken) {
        headers.Authorization = savedToken.startsWith('c3Nv')
            ? `Bearer ${savedToken}`
            : `Basic ${savedToken}`;
    }
    
    return fetch(url, {
        headers
    });
}

export async function fetchFilters() {
    const results: any[][] = await fetchResults(
        [
            fetchWithAuthorization(archiverUrl() + '/filter?sort=name')
        ]);
    // console.log (results);
    return results[0];
}

export async function sendData(method: string, uri: string, data: any):
    Promise<any[]> {
    const savedToken = window.localStorage.getItem('dbmar-token') || '';
    const headers: Record<string, string> = {
        'Content-type': 'application/json; charset=UTF-8'
    };

    if (savedToken) {
        headers.Authorization = savedToken.startsWith('c3Nv')
            ? `Bearer ${savedToken}`
            : `Basic ${savedToken}`;
    }
    
    return await fetchResults(
        [
            fetch(archiverUrl() + uri,
                {
                    method: method,
                    body: JSON.stringify(data),
                    headers
                })
        ]);
}

function shouldUseSchema(type: string): boolean {
    // React thinks useSchema is a hook, which was my first choice.
    return type === INSTANCE_TYPE_ORACLE;
}

export function DatabaseLabel(type: string): string {
    return shouldUseSchema(type) ? 'Schema' : 'Database';
}

export function DatabasesLabel(type: string): string {
    return shouldUseSchema(type) ? 'Schemas' : 'Databases';
}

export function databaseLabel(type: string): string {
    return shouldUseSchema(type) ? 'schema' : 'database';
}

export function databasesLabel(type: string): string {
    return shouldUseSchema(type) ? 'schemas' : 'databases';
}

export function formatNumber(n: number): string {
    return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}


export function formatSqlStatement(statement: string, instanceType = 'unknown') {
    let result: string = statement;
    if (statement.length > 0) {
        try {
            if (instanceType === 'mongodb') {
                // Mongo statements are JSON command documents.
                result = JSON.stringify(JSON.parse(statement), null, 2);
                return result;
            }
            result = sqlFormatter.format(statement, { language: getQueryLanguageFromInstanceType(instanceType) });
        } catch (error: any) {
        }
    }
    return result;
}

export interface Step {
    key: string,
    data: any,
    children: Step[]
}

export function getStepDetails(steps: Step[],
                               selectedStepKey: TreeTableSelectionKeys): any[] {
    function findStep(steps: Step[]): Step | null {
        for (const step of steps) {
            if (step.key === selectedStepKey) {
                // console.log ('Found key ' + selectedStepKey);
                return step;
            } else {
                const child: Step | null = findStep(step.children)

                if (child) {
                    return child;
                }
            }
        }

        // console.log ('Did not find key ' + selectedStepKey);
        return null;
    }

    const details: any[] = [];
    const step: Step | null = findStep(steps);

    if (step) {
        for (const field in step.data) {
            details.push(
                {
                    name: field,
                    value: step.data[field]
                });
        }
    }

    return details;
}

export function getTime(isoDate: string, isoFormat = true): string | number {
    // Convert the timestamp to UTC milliseconds.
    const t: any[] = isoDate.split(/[- :+]/);
    const date: Date = new Date(t[0], t[1] - 1, t[2], t[3], t[4], t[5]);
    const time: number = date.getTime() - date.getTimezoneOffset() * 60000;

    return isoFormat ? time : dayjs.default(date.getTime()).format('YYYY/MM/DD HH:mm:ss');
}

export function timeSeries(tdata: any[], field1: string, field2: string, isoFormat = true): any[] {
    let result: any = [];

    for (let i = 0; i < tdata.length; i++) {
        try {
            // result[i] = [getTime(tdata[i][field1], isoFormat), Math.round((tdata[i][field2] * 100)) / 100];
            result[i] = [getTime(tdata[i][field1], isoFormat), Math.round((tdata[i][field2] * 100)) / 100];
        } catch (ignore) {
        }
    }

    return result;
}

export function timeSeriesWithGranularity(tdata: any[], field2: string): any[] {
    let result: any = [];

    for (let i = 0; i < tdata.length; i++) {
        try {
            result[i] = [Helper.parseTimesliceWithTz(String(tdata[i].timeslice)).valueOf()

                , tdata[i][field2]];
        } catch (ignore) {
        }
    }
    return result;
}


export function prettifyChangesDetailsString(str: string | undefined | null) {
    if (str === 'NULL' || str === null || str === undefined) {
        return 'NULL'
    }

    str = str || ''
    return str.length > 30 ? `'${str.substring(0, 30)}...'` : `'${str.substring(0, 30)}'`
}

export const getQueryLanguageFromInstanceType = (instanceType: string) => {
    try {
        return INSTANCE_TYPE_TO_QUERY_LANGUAGE[instanceType]
    } catch (error: any) {
        return 'tsql'
    }
}

export const formatStatement = (statements: string | any[], instanceType: string) => {

    let result: string = '';
    if (statements.length > 0) {
        try {
            result = statements[0].sqltext;
            result = formatSqlStatement(result, instanceType)
        } catch (error: any) {
            console.error('Failed to format SQL', error);
        }
    }

    return result
}

export const copyToClipboard = (text: string, setToolTip: Function) => {
    const input = document.body.appendChild(document.createElement("textarea"));
    input.value = text;
    input.focus();
    input.select();
    try {
        document.execCommand('copy');
        input?.parentNode?.removeChild(input);
        setToolTip()
    } catch (ignore) {
    }
}

export const getToolTipPosition = (event: any) => {
    const target = event.currentTarget || event.target;
    const panel = target?.closest?.('.object-browser-details-panel, .agent-server-details-panel, .instance-detail-slideout');
    
    if (panel) {
        const panelRect = panel.getBoundingClientRect();
        const pointerX = event.clientX;
        return pointerX - panelRect.left + 10;
    }
    
    // Default behavior for main page content
    const getMenu = document.getElementById('menu-side')
    const menuWidth = getMenu?.clientWidth || 150;
    const pointerX = event.pageX;
    return pointerX - menuWidth + 10
}


export const getAction = (actionId: string | undefined, setAction: Function, setDisabled: Function) => {
    switch (actionId) {
        case 'create':
            setAction(ACTIONS.CREATE);
            break;

        case 'edit':
            setAction(ACTIONS.UPDATE);
            break;

        case 'delete':
            setAction(ACTIONS.DELETE);
            setDisabled(true);
            break;

        default:
            console.error('Unknown action requested', actionId);
            break;
    }
}

export const stringFormatting = (val: string) => {
    const mapping = {
        'cockroachdb': 'CockroachDB',
        'db2': 'DB2',
        'informix': 'Informix',
        'mysql': 'MySQL',
        'postgresql': 'PostgreSQL',
        'oracle': 'Oracle',
        'saphana': 'SAP HANA',
        'sqlserver': 'SQL Server',
        'sybase': 'SAP (Sybase) ASE',
        'activesessions': 'Active Sessions',
        'idlesessions': 'Idle Sessions',
        'memoryutilisation': 'Memory Utilisation',
        'ioutilisation': 'I/O Utilisation',
        'cpuutilisation': 'CPU Utilisation',
        'diskutilisation': 'Disk Utilisation',
        'waittime': 'Wait Time',
        'executions': 'Executions',
        'duration': 'Duration',
        'batchexecutions': 'Batch Executions',
    }
    // @ts-ignore
    const isException = mapping[val]
    return isException || val
}

export const capitalizeString = (val: string): string => {
    const lower = val.toLowerCase();

    if (val.includes(' ')) {
        const wordList = val.split(' ')
        return wordList.map((word: string) => capitalizeString(word)).join(' ')
    }

    return val.charAt(0).toUpperCase() + lower.slice(1);
}

export const getValidClass = (hasError: any, value: string | number, isDelete: boolean) => {
    return isDelete ? '' : hasError ? 'is-invalid' : value?.toString().trim() ? 'is-valid' : ''
}

export const findEmailIntegrationValue = (name: any, list: any[]) => {
    let prefix = name === 'starttls.enable' ? 'mail.smtp.starttls.' : 'mail.smtp.'
    prefix = name === 'url' ? 'mail.smtp.dbmarlin.' : prefix
    const findElem = list.find((elem: { name: string; }) => elem?.name === `${prefix}${name}`) || {}
    return findElem ? findElem?.value === 'true' ? true : findElem?.value === 'false' ? false : findElem?.value : undefined
}

export const findIntegrationValue = (name: any, list: any[], key: string) => {
    const findElem = list.find((elem: { name: string; }) => elem?.name === `${key}.${name}`) || {}
    return findElem ? findElem?.value === 'true' ? true : findElem?.value === 'false' ? false : findElem?.value : undefined
}

// Constants for icon classes
const ICON_CLASSES = {
    ALERT: 'fal red-icon chart-icon fa-exclamation-triangle fa-fw',
    ALERT_BY_SEVERITY: {
        0: 'fal green-icon chart-icon fa-exclamation-triangle fa-fw', // OK / resolved
        1: 'fal red-icon chart-icon fa-exclamation-triangle fa-fw',   // Critical
        2: 'fal amber-icon chart-icon fa-exclamation-triangle fa-fw', // Warning
        3: 'fal blue-icon chart-icon fa-exclamation-triangle fa-fw'   // Info
    } as Record<number, string>,
    CHANGE: 'fal chart-icon fa-exchange fa-fw',
    EVENT: 'fal chart-icon fa-calendar fa-fw',
    PLAN: 'fal chart-icon fa-ruler-triangle fa-fw'
} as const;

// Human-readable severity labels — used in tooltips for status alerts where a comparison
// against a threshold doesn't make sense.
export const SEVERITY_LABEL: Record<number, string> = {
    0: 'Normal',
    1: 'Critical',
    2: 'Warning',
    3: 'Info'
};

// Pick the most severe alert at this timeslice. 1 (Critical) > 2 (Warning) > 3 (Info) > 0 (OK/resolved).
// Returns 1 as the safe default when severity is missing or out of range.
const getWorstAlertSeverity = (alerts: any[]): number => {
    let worstActive: number | undefined; // 1-3
    let sawZero = false;
    alerts.forEach(item => {
        const sev = Number(item?.alert?.severity);
        if (!Number.isFinite(sev)) return;
        if (sev === 0) { sawZero = true; return; }
        if (sev < 1 || sev > 3) return;
        if (worstActive === undefined || sev < worstActive) worstActive = sev;
    });
    if (worstActive !== undefined) return worstActive;
    if (sawZero) return 0;
    return 1;
};

const _getTotalChanges = (val: number): string => {
    return val > 1 ? val.toString() : '';
};

const generateIconSpan = (length: number, iconClass: string): string => {
    if (!length) return '<span class="empty-icon">&nbsp;</span>';
    
    const count = _getTotalChanges(length);
    return `<span><i class="${iconClass}"></i>${count ? `<sup class="text-secondary">${count}</sup>` : ''}</span>`;
};

const generateTooltipItems = (texts: string[], iconClass: string): string => {
    if (texts.length === 0) return '';

    const MAX_DISPLAY = 5;
    const MAX_TEXT_LENGTH = 80;

    const items = texts.slice(0, MAX_DISPLAY).map(text => {
        const truncatedText = text.length > MAX_TEXT_LENGTH ? `${text.substring(0, MAX_TEXT_LENGTH)}...` : text;
        return `<p><i class="${iconClass}"></i> ${truncatedText}</p>`;
    });

    if (texts.length > MAX_DISPLAY) {
        items.push('<p>[...]</p>');
    }

    return items.join('');
};

// Like generateTooltipItems but each row picks its own icon class — used for alerts so the
// tooltip rows show their individual severity colours instead of a single shared colour.
const generateAlertTooltipItems = (entries: { text: string; iconClass: string }[]): string => {
    if (entries.length === 0) return '';

    const MAX_DISPLAY = 5;
    const MAX_TEXT_LENGTH = 80;

    const items = entries.slice(0, MAX_DISPLAY).map(({ text, iconClass }) => {
        const truncated = text.length > MAX_TEXT_LENGTH ? `${text.substring(0, MAX_TEXT_LENGTH)}...` : text;
        return `<p><i class="${iconClass}"></i> ${truncated}</p>`;
    });

    if (entries.length > MAX_DISPLAY) {
        items.push('<p>[...]</p>');
    }

    return items.join('');
};

const getRedirectLink = (
    statementId: string | undefined,
    resourceId: number | undefined,
    period: string | undefined
): string => {
    const pathname = window.location.pathname;
    
    if (statementId) {
        // Check if ID has type prefix (e.g., "batch:123" or "groupedbatch:456")
        const prefixMatch = statementId.match(/^(batch|groupedbatch|statement|groupedstatement):(.+)$/);
        if (prefixMatch) {
            const [, type, id] = prefixMatch;
            return `/instances/${resourceId}/activity/${type}/${id}/events?${period}`;
        }
        
        // If pathname already contains the statementId, replace last segment with 'events'
        if (pathname.includes(statementId)) {
            const segments = pathname.split('/');
            segments[segments.length - 1] = 'events';
            return `${segments.join('/')}?${period}`;
        }
        
        // Side panel case: pathname doesn't have statementId, construct the URL
        const isGrouped = pathname.includes('grouped');
        const isBatch = pathname.includes('batch');
        const type = isGrouped ? (isBatch ? 'groupedbatch' : 'groupedstatement') : (isBatch ? 'batch' : 'statement');
        return `/instances/${resourceId}/activity/${type}/${statementId}/events?${period}`;
    }
    
    const isHostsPage = pathname.includes('/hosts/');
    const route = isHostsPage ? 'hosts' : 'instances';
    
    return `/${route}/${resourceId}/events?${period}`;
};

const resolveEventIconClass = (type: string, eventTypes: EventType[]): string => {
    const et = eventTypes.find(e => e.title === type);
    return et?.htmlIconCode ? `${et.htmlIconCode} fa-fw` : ICON_CLASSES.EVENT;
};

const generateEventIconSpans = (events: any[], eventTypes: EventType[]): string => {
    if (!events.length) return '<span class="empty-icon">&nbsp;</span>';
    const firstType = events[0]?.custom?.type || '';
    const iconClass = resolveEventIconClass(firstType, eventTypes) + ' chart-icon';
    return generateIconSpan(events.length, iconClass);
};

const generateEventTooltipItems = (events: any[], eventTypes: EventType[]): string => {
    if (!events.length) return '';

    const MAX_DISPLAY = 5;
    const MAX_TEXT_LENGTH = 80;

    const items = events.slice(0, MAX_DISPLAY).map(item => {
        const type = item.custom?.type || '';
        const iconClass = resolveEventIconClass(type, eventTypes);
        const text = `${type}: ${item.custom?.name} - ${item.custom?.description}`;
        const truncated = text.length > MAX_TEXT_LENGTH ? `${text.substring(0, MAX_TEXT_LENGTH)}...` : text;
        return `<p><i class="${iconClass}"></i> ${truncated}</p>`;
    });

    if (events.length > MAX_DISPLAY) {
        items.push('<p>[...]</p>');
    }

    return items.join('');
};

export const combinedIcons = (
    timesliceEvents: any[],
    timesliceChanges: any[],
    timesliceAlerts: any[],
    timeslicePlans: any[],
    period?: string,
    isReport?: boolean,
    resourceId?: number,
    statementId?: string,
    eventTypes: EventType[] = [],
    reportTab?: string
): string => {
    // Per-alert tooltip entries — each picks its own icon class by the row's severity.
    // Format matches the Event History table (InstanceEvents.tsx):
    //   status alerts:  <rule-name> <severity>
    //   other alerts:   <rule-name> <severity> (<value><units> <op> <threshold><units>)
    const alertEntries = timesliceAlerts.map(item => {
        const sev = Number(item?.alert?.severity);
        const safeSev = Number.isFinite(sev) ? sev : 1;
        const iconClass = ICON_CLASSES.ALERT_BY_SEVERITY[safeSev] ?? ICON_CLASSES.ALERT;
        const ruleName = item.alert.rulename
            || item.alert.alertname
            || item.alert.name
            || item.alert.statistic
            || 'Alert';
        const severityLabel = (SEVERITY_LABEL[safeSev] ?? 'Alert').toLowerCase();
        const isStatusAlert = item.alert.metrictype === 'status' || item.alert.statistic === 'sensorstatus';

        let text: string;
        if (isStatusAlert) {
            text = `${ruleName} ${severityLabel}`;
        } else {
            const units = item.alert.units || '';
            // Pick the operator the way InstanceEvents.tsx does — based on severity and direction.
            let op = '=';
            if (safeSev > 0) {
                op = item.alert.decreasing?.toString() === 'true' ? '<' : '>';
            } else {
                op = item.alert.decreasing?.toString() === 'true' ? '>' : '<';
            }
            const value = item.alert.changeTo ?? item.alert.newvalue ?? '';
            const threshold = item.alert.threshold ?? '';
            text = `${ruleName} ${severityLabel} (${value}${units} ${op} ${threshold}${units})`;
        }
        return { text, iconClass };
    });

    const changesTexts = timesliceChanges.map(item => `${item.change?.type}: ${item.change?.name}`);
    const plansTexts = timeslicePlans.map(item => `${item.plan.sqlplan.slice(0, 50)}...`);

    // Single chart icon uses the worst severity present at this timeslice.
    const chartAlertIconClass = ICON_CLASSES.ALERT_BY_SEVERITY[getWorstAlertSeverity(timesliceAlerts)] ?? ICON_CLASSES.ALERT;

    // Build HTML using array for better performance
    const iconSpans = [
        generateIconSpan(timesliceAlerts.length, chartAlertIconClass),
        generateIconSpan(timesliceChanges.length, ICON_CLASSES.CHANGE),
        generateEventIconSpans(timesliceEvents, eventTypes)
        // Plan count icon hidden from the chart markers - uncomment to restore it.
        // generateIconSpan(timeslicePlans.length, ICON_CLASSES.PLAN)
    ].join('');

    const tooltipItems = [
        generateAlertTooltipItems(alertEntries),
        generateTooltipItems(changesTexts, ICON_CLASSES.CHANGE),
        generateEventTooltipItems(timesliceEvents, eventTypes),
        generateTooltipItems(plansTexts, ICON_CLASSES.PLAN)
    ].join('');

    // In reports the events table keeps its own Changes/Events/Alerts filter selected across
    // marker clicks, so also work out which one this marker belongs to (only when it's a single
    // type - a marker mixing e.g. an alert and a change, or alerts and events, can't be narrowed
    // to one filter and falls back to "all") so the table can be pointed at the same filter and
    // the marker's rows aren't hidden behind it.
    const activeCategoryCount = [timesliceAlerts.length, timesliceChanges.length, timesliceEvents.length].filter(count => count > 0).length;
    let eventTypeFilter = 'all';
    if (activeCategoryCount === 1) {
        if (timesliceAlerts.length) {
            eventTypeFilter = 'alert';
        } else if (timesliceChanges.length) {
            eventTypeFilter = 'change';
        } else if (timesliceEvents.length) {
            eventTypeFilter = 'custom';
        }
    }

    // Determine link href. In reports, jump to the tab for this marker's period, carrying the
    // filter as a query string on the hash (Highcharts strips unknown data-* attributes from
    // useHTML content via its AST sanitizer, so it can't be passed as a separate attribute).
    const href = isReport
        ? (reportTab ? `#changes-${reportTab}${eventTypeFilter !== 'all' ? `?filter=${eventTypeFilter}` : ''}` : '#collapseTable')
        : getRedirectLink(statementId, resourceId, period);

    // Build final HTML
    const hasEvents = timesliceAlerts.length || timesliceChanges.length || timesliceEvents.length;
    const eventLink = hasEvents ? `<p class="mt-2"><a href="${href}">See Events for more details</a></p>` : '';

    return `<div class="hover-trigger">${iconSpans}</div><div class="event-tooltip">${tooltipItems}${eventLink}</div>`;
};

export const mergeArraysByProp = (a: any[], b: any[], prop: string) => {
    const reduced = [];
    for (let i = 0; i < a.length; i++) {
        const aitem = a[i];
        let found = false;

        for (let ii = 0; ii < b.length; ii++) {
            if (aitem[prop] === b[ii][prop]) {
                found = true;
                break;
            }
        }

        if (!found) {
            reduced.push(aitem);
        }
    }

    return reduced.concat(b);
}

export const getDefaultPageSize = () => {
    const value = JSON.parse(window.localStorage.getItem('pageSize') || '10')
    return Number(value)
}



export const getWaitEventLink = (waitEvent: string) => {
    // Some processing is need of the wait-event to match the URL on the docs.dbmarlin.com side.
    // Swap slash for dash and strip brackets and percent characters
    let waitEventNew = waitEvent.replace("/", "-")

    // For CockroachDB the event 'executing (XXX.XX%)' can be any percentage but we have just 1 KB article for XXX.XX%
    var pattern = /executing \(.*\)/i;
    waitEventNew = waitEventNew.replace(pattern, "executing (XXX.XX%)");

    waitEventNew = waitEventNew.replace("(", "")
    waitEventNew = waitEventNew.replace(")", "")
    waitEventNew = waitEventNew.replace("%", "")
    waitEventNew = encodeURIComponent(waitEventNew)
    return (waitEventNew)
}

export function instanaTechnology(instanceType: string): string {
    // Map DBmarlin instance type to Instana technology.
    const map: any =
        {
            cockroachdb: 'cockroachDatabase',
            db2: 'db2Database',
            mysql: 'mySqlDatabase',
            postgresql: 'postgreSqlDatabase',
            oracle: 'oracleDB',
            sqlserver: 'msSqlDatabase'
        };

    return map[instanceType] || instanceType;
}

export const determineTimeUnit = (current_interval_filter_ms: number) => {
    if (current_interval_filter_ms <= 120000) {
        return 'minute'; // Less than 2 minutes
    } else if (current_interval_filter_ms <= 600000) {
        return 'minutes_10'; // Less 10 minutes
    } else if (current_interval_filter_ms <= 1800000) {
        return 'minutes_30'; // Less 30 minutes
    } else if (current_interval_filter_ms <= 3600000) {
        return 'hours_1'; // Less than 1 hour
    } else if (current_interval_filter_ms <= 7200000) {
        return 'hours_2'; // Less than 2 hours
    } else if (current_interval_filter_ms <= 28800000) {
        return 'hours_8'; // Less than 8 hours
    } else if (current_interval_filter_ms <= 86400000) {
        return 'day'; // Less than 1 day
    } else if (current_interval_filter_ms <= 608400000) {
        return 'week_1'; // Less than 1 week
    } else if (current_interval_filter_ms <= 3024000000) {
        return 'weeks_5'; // Less than 5 weeks
    } else if (current_interval_filter_ms <= 12096000000) {
        return 'weeks_20'; // Less than 20 weeks
    } else {
        return 'weeks'; // 20 weeks or more
    }
};

/** @deprecated Use getConvertedTimeToUTC from helpers/timeRangeUtils instead. */
export const getConvertedTimeToUTC = _getConvertedTimeToUTC;

export const getMinMAx = (timeRangeContext: any) => {
    const from = timeRangeContext.timeRange?.from?.valueOf?.();
    const to = timeRangeContext.timeRange?.to?.valueOf?.();
    return {
        minX: Number.isFinite(from) ? from : undefined,
        maxX: Number.isFinite(to) ? to : undefined
    };
};

export const updateExpiration = () => {
    const newExpirationDate = new Date();
    // newExpirationDate.setMinutes(newExpirationDate.getMinutes() + 10);
    newExpirationDate.setDate(newExpirationDate.getDate() + EXPIRATION_DAYS);
    window.localStorage.setItem(EXPIRATION_KEY, newExpirationDate.toISOString());
};

export const getAggregateInt = (agg: string, options: InstanceOptions[], instances: InstanceTarget[]) => {
    switch (agg) {
        case 'auto':
            return Math.max(...options.map(option => Helper.getInterval(dayjs.default(option.periodStart), dayjs.default(option.periodEnd))))
        case 'timeslice':
            return Math.max(...options.map(option => instances.find(i => i.id === option.instanceId)?.batchintervalseconds || 0)) * 1000
        default:
            return parseInt(agg, 10)
    }
}

/** Format period for table/card headers - same style as Helper.getFormattedPeriodHeadingFromDate but in selected timezone. */
export const formatPeriodHeadingFromDate = (dateFrom: Date, dateTo: Date, timezone: string): string => {
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const from = dayjs.tz(dateFrom, tz);
    const to = dayjs.tz(dateTo, tz);

    if (from.isSame(to, 'year')) {
        if (from.isSame(to, 'month')) {
            if (from.isSame(to, 'day')) {
                return `${from.format('Do MMM HH:mm')} to ${to.format('HH:mm')}`;
            }
            return `${from.format('Do HH:mm')} to ${to.format('Do MMM HH:mm')}`;
        }
        return `${from.format('Do MMM HH:mm')} to ${to.format('Do MMM HH:mm')}`;
    }
    return `${from.format('Do MMM YYYY HH:mm')} to ${to.format('Do MMM YYYY HH:mm')}`;
};

export const formatPeriod = (instanceOptions: InstanceOptions, timezone?: string) => {
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startDate = dayjs.tz(instanceOptions.periodStart, tz);
    const endDate = dayjs.tz(instanceOptions.periodEnd, tz);

    const sameDay = startDate.isSame(endDate, "day");
    const sameMonth = startDate.isSame(endDate, "month");
    const sameYear = startDate.isSame(endDate, "year");

    const start = startDate.format("D MMM HH:mm");

    let end: string;
    if (!sameYear) {
        end = endDate.format("D MMM YYYY HH:mm");
    } else if (!sameMonth) {
        end = endDate.format("D MMM HH:mm");
    } else if (!sameDay) {
        end = endDate.format("D MMM HH:mm");
    } else {
        end = endDate.format("HH:mm");
    }

    return `${start} → ${end}`;
};

export const sortStrings = (rowA: any, rowB: any, columnId: string) => {
    const valueA = rowA.values[columnId] ?? '';
    const valueB = rowB.values[columnId] ?? '';

    return String(valueA).localeCompare(String(valueB));
};
